"use client";

import { Minus, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MetalLaserEngravingFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "What metals can be laser engraved?",
    answer: "We specialize in engraving a wide range of industrial metals, including stainless steel, aluminum, brass, copper, and titanium. Our high-precision lasers ensure permanent, high-contrast marks suitable for harsh environments."
  }, {
    question: "Is the engraving permanent?",
    answer: "Yes. Our laser marking process creates a permanent, indelible mark that is resistant to chemicals, heat, abrasion, and UV exposure, making it ideal for long-term asset identification and compliance."
  }, {
    question: "Can you handle custom logos and serial numbers?",
    answer: "Absolutely. We can engrave complex logos, sequential serial numbers, barcodes, QR codes, and custom text. We work with your vector files to ensure exact replication of your branding or technical specifications."
  }, {
    question: "What is the turnaround time for metal engraving?",
    answer: "Turnaround times vary based on project volume and complexity. We prioritize efficiency and offer expedited services for urgent submittal packages. Contact our team for a specific timeline on your order."
  }, {
    question: "Do you provide proofs before production?",
    answer: "Yes, for custom projects, we provide digital proofs to ensure all text, logos, and layouts meet your requirements before we begin the full production run."
  }, {
    question: "Are your engravings compliant with industry standards?",
    answer: "We ensure all markings meet relevant industry standards for legibility and durability, including those required for electrical, mechanical, and aerospace applications."
  }, {
    question: "How do I submit my design files?",
    answer: "You can upload your vector files (AI, DXF, or EPS) directly through our contact form or email them to our production team. We will review them for compatibility and provide a quote."
  }, {
    question: "Can you engrave on curved or irregular surfaces?",
    answer: "Our advanced laser systems are equipped with rotary attachments and specialized optics, allowing us to engrave on cylindrical, curved, and irregular metal surfaces with high precision."
  }, {
    question: "Do you offer bulk order discounts?",
    answer: "Yes, we provide competitive pricing for high-volume production runs. Please reach out to our sales department to discuss your project requirements and receive a custom volume-based quote."
  }];
  return <section data-section-id="575" id="metal-laser-engraving-faq" className="bg-background py-24 px-6 md:px-8">
      <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="text-center mb-16 md:mb-20">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-6">
            Technical Support
          </p>
          <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.05] tracking-tight">
            Frequently Asked Questions
          </h2>
        </div>

        {}
        <Accordion type="single" collapsible className="w-full border-t border-border">
          {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group">
              <AccordionTrigger className="py-6 hover:no-underline [&>svg.lucide-chevron-down]:hidden [&[data-state=open]>div>.plus-icon]:hidden [&[data-state=closed]>div>.minus-icon]:hidden [&[data-state=open]>span]:text-primary text-left">
                <span className="font-serif font-default text-lg md:text-xl text-foreground pr-6 leading-snug transition-colors duration-300">
                  {faq.question}
                </span>
                <div className="flex-shrink-0 mt-1">
                  <Plus className="plus-icon size-5 text-foreground transition-all duration-300" strokeWidth={1} />
                  <Minus className="minus-icon size-5 text-primary transition-all duration-300" strokeWidth={1} />
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <p className="font-sans text-base md:text-lg text-muted-foreground max-w-2xl leading-[1.7]">
                  {faq.answer}
                </p>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>

        {}
        <div className="mt-16 md:mt-20 flex justify-center">
          <div className="bg-card px-10 py-8 md:px-12 md:py-10 rounded-lg text-center max-w-md w-full">
            <h3 className="font-serif font-default text-2xl md:text-3xl text-foreground mb-3">
              Need further assistance?
            </h3>
            <p className="font-sans text-sm text-muted-foreground mb-5 leading-relaxed">
              Our technical team is ready to help with your specific metal marking requirements.
            </p>
            <Link to="/ContactPage" className="inline-flex items-center gap-2 font-sans text-base text-primary hover:opacity-80 transition-opacity duration-300 group">
              <span className="border-b border-primary/40 pb-0.5">
                Contact our support team
              </span>
              <span className="transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}